import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList from "@insite/content-library/Components/CardList";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import Button from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import OrderHistoryProductQuantityOrdered from "@store/Components/Rma/OrderHistoryProductQuantityOrdered";
import RmaUploadFile from "@store/Components/Rma/RmaUploadFile";
import ProductDescription, { ProductDescriptionStyles } from "@store/Overrides/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@store/Overrides/Components/ProductImage";
import { getRmaLinesRaw, getRmaNotes, getRmaProductInfos, getRmaReasons } from "@store/Selectors/Rma/RmaSelectors";
import FetchRmaReasons from "@store/Store/Data/RmaReasons/Handlers/FetchRmaReasons";
import changeRmaLineInvoiceNumber from "@store/Store/Data/RmaRequest/Handlers/ChangeRmaLineInvoiceNumber";
import changeRmaLineProductQty from "@store/Store/Data/RmaRequest/Handlers/ChangeRmaLineProductQty";
import changeRmaLineSerialNumber from "@store/Store/Data/RmaRequest/Handlers/ChangeRmaLineSerialNumber";
import changeRmaNotes from "@store/Store/Data/RmaRequest/Handlers/ChangeRmaNotes";
import changeRmaReasons from "@store/Store/Data/RmaRequest/Handlers/ChangeRmaReasons";
import removeRmaLineProduct from "@store/Store/Data/RmaRequest/Handlers/RemoveRmaLineProduct";
import addProduct from "@store/Store/Pages/Rma/Handlers/AddProduct";
import changeProductQty from "@store/Store/Pages/Rma/Handlers/ChangeProductQty";
import removeProduct from "@store/Store/Pages/Rma/Handlers/RemoveProduct";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { OrderHistoryProductModel, RmaLineModel } from "@store/Types/ApiModels";
import productInContext from "@store/utilities/productInContext";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { v4 as uuidv4 } from "uuid";

const mapStateToProps = (state: StoreApplicationState) => {
    return {
        rmaReasons: getRmaReasons(state),
        rmaLineModels: getRmaLinesRaw(state),
        productInfos: getRmaProductInfos(state),
        currencySymbol: state.context.session.currency?.currencySymbol || "",
        rmaNotes: getRmaNotes(state),
        state,
    };
};

const mapDispatchToProps = {
    changeProductQty,
    changeRmaLineProductQty,
    changeRmaNotes,
    changeRmaReasons,
    changeRmaLineSerialNumber,
    removeProduct,
    removeRmaLineProduct,
    FetchRmaReasons,
    changeRmaLineInvoiceNumber,
    addProduct,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RmaOrderHistoryProductListStyles {
    wrapper?: InjectableCss;
    cardContainer?: CardContainerStyles;
    gridContainer?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    brandAndProductDescriptionGridItem?: GridItemProps;
    brand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productNumberText?: TypographyPresentationProps;
    availability?: ProductAvailabilityStyles;
    priceAndQuantityGridItem?: GridItemProps;
    priceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    price?: ProductPriceStyles;
    quantityGridItem?: GridItemProps;
    quantityOrdered?: TextFieldProps;
    extendedPriceGridItem?: GridItemProps;
    extendedPriceText?: TypographyPresentationProps;
    productActionsGridItem?: GridItemProps;
    removeProductClickable?: ClickableProps;
    removeProductIcon?: IconPresentationProps;
    addToListLink?: ProductAddToListLinkStyles;
    totalText?: TypographyPresentationProps;
    editableNotesTextArea?: TextAreaProps;
    gridWrapper?: InjectableCss;
    notesLine?: GridItemProps;
    uploadFileGridItem?: GridItemProps;
    label?: TypographyPresentationProps;
    returnReasonGridItem?: GridItemProps;
    invoiceNumberTextField?: TextFieldProps;
    serialNumberTextField?: TextFieldProps;
}

export const rmaOrderHistoryProductListStyles: RmaOrderHistoryProductListStyles = {
    wrapper: {
        css: css`
            padding-bottom: 20px;
            margin-top: 20px;
        `,
    },
    gridWrapper: {
        css: css`
            width: 100%;
        `,
    },
    cardContainer: {
        cardDivider: {
            css: css`
                margin: 0 20px;
                width: 100%;
                border-top: 1px solid ${getColor("common.border")};
                &:last-child {
                    border-bottom: 1px solid ${getColor("common.border")};
                }
            `,
        },
    },
    notesLine: {
        width: 12,
        css: css`
            padding-top: 30px;
        `,
    },
    uploadFileGridItem: {
        width: 12,
        css: css`
            padding-top: 30px;
        `,
    },
    editableNotesTextArea: {
        css: css`
            min-width: 100%;
            max-width: 100%;
            min-height: 30px;
        `,
    },
    productImageGridItem: {
        width: [12, 2, 2, 2, 2],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0;
                        padding-right: 0;
                        font-size: 10px;
                    `,
                    css`
                        padding-left: 0;
                        padding-right: 0;
                        font-size: 10px;
                    `,
                    css`
                        padding-left: 0;
                    `,
                    css`
                        padding-left: 0;
                    `,
                    css`
                        padding-left: 0;
                    `,
                ])}
        `,
    },
    productInfoGridItem: { width: [12, 9, 5, 5, 5] },
    returnReasonGridItem: { width: [9, 9, 3, 3, 3] },
    productActionsGridItem: {
        css: css`
            align-items: center;
            justify-items: center;
            flex-direction: column;
        `,
        width: [3, 3, 2, 2, 2],
    },
    brandAndProductDescriptionGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    availability: {
        container: {
            css: css`
                margin-top: 10px;
            `,
        },
    },
    priceAndQuantityGridItem: { width: [12, 12, 12, 6, 6] },
    priceGridItem: {
        width: [12, 12, 4, 6, 6],
        css: css`
            flex-direction: column;
            row-gap: 16px;
            justify-content: center;
            row-gap: 16px;
        `,
    },
    quantityGridItem: { width: [6, 6, 5, 6, 6] },
    quantityOrdered: {
        labelPosition: "left",
        labelProps: {
            css: css`
                width: auto;
                display: inline-flex;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                width: 70px;
            `,
        },
    },
    serialNumberTextField: {
        labelPosition: "top",
        labelProps: {
            css: css`
                width: fit-content;
                display: inline-flex;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                width: auto;
            `,
        },
    },
    extendedPriceGridItem: { width: [6, 6, 3, 6, 6] },
    extendedPriceText: {
        css: css`
            font-weight: bold;
            align-self: center;
        `,
    },
    removeProductClickable: {
        css: css`
            margin-left: auto;
            margin-right: auto;
            margin-bottom: 15px;
        `,
    },
    removeProductIcon: { src: XCircle },
    invoiceNumberTextField: {
        labelPosition: "top",
        labelProps: {
            css: css`
                width: fit-content;
                display: inline-flex;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                width: auto;
            `,
        },
    },
};

const styles = rmaOrderHistoryProductListStyles;
const StyledSection = getStyledWrapper("section");

const RmaOrderHistoryProductList = ({
    rmaLineModels,
    productInfos,
    changeProductQty,
    changeRmaLineProductQty,
    removeRmaLineProduct,
    changeRmaNotes,
    changeRmaReasons,
    changeRmaLineSerialNumber,
    removeProduct,
    FetchRmaReasons,
    rmaReasons,
    rmaNotes,
    changeRmaLineInvoiceNumber,
    addProduct,
}: Props) => {
    const toasterCtx = useToaster();

    useEffect(() => {
        if (rmaReasons.length === 0) {
            FetchRmaReasons({});
        }
    }, []);

    const removeProductHandler = (lineId: string) => {
        removeProduct({ lineId });
        removeRmaLineProduct({ LineId: lineId });
    };

    const notesBlurHandler = (event: React.FocusEvent<HTMLTextAreaElement>) => {
        changeRmaNotes({ rmaNotes: event.currentTarget.value });
    };

    const notesChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        changeRmaNotes({ rmaNotes: event.currentTarget.value });
    };

    const getCurrentLineReasonValue = (lineId: string) => {
        const currentRmaLine = rmaLineModels.find((o: RmaLineModel) => o.LineId === lineId);
        if (!currentRmaLine || !currentRmaLine.ReturnReason) {
            return;
        }
        return rmaReasons[currentRmaLine.ReturnReason] ?? "";
    };

    const reasonsHandler = (reason: string, lineId: string) => {
        const reasonKey = Object.keys(rmaReasons).find(key => rmaReasons[key] === reason);
        changeRmaReasons({ LineId: lineId, ReturnReason: reasonKey ?? "" });
    };

    const addAgainHandler = (product: OrderHistoryProductModel) => {
        const productToAdd = { ...product, lineId: uuidv4(), qtyOrdered: 0 };
        addProduct({ selectedProduct: productToAdd });
        toasterCtx.addToast({ body: translate("Item Added"), messageType: "success" });
    };

    const productListDisplay = productInfos.map((product, index) => {
        if (!product || index >= productInfos.length || !product.autocompleteItemModel.id) {
            return;
        }

        const productContext = productInContext(product, qtyOrdered => {
            changeProductQty({
                qtyOrdered,
                lineId: product.lineId,
                product,
            });
            changeRmaLineProductQty({
                LineId: product.lineId,
                ReturnedQuantity: qtyOrdered,
            });
        });

        return (
            <ProductContext.Provider value={productContext} key={product.lineId}>
                <CardContainer
                    extendedStyles={styles.cardContainer}
                    data-test-selector={`RmaOrderHistoryProductList_LineId_${product.lineId}`}
                >
                    <StyledWrapper {...styles.gridWrapper}>
                        <GridContainer {...styles.gridContainer}>
                            <GridItem {...styles.productImageGridItem}>
                                <ProductImage product={productContext} extendedStyles={styles.productImage} />
                            </GridItem>
                            <GridItem {...styles.productInfoGridItem}>
                                <GridContainer {...styles.productInfoContainer}>
                                    <GridItem {...styles.brandAndProductDescriptionGridItem}>
                                        <Typography {...styles.productNumberText}>
                                            {product.autocompleteItemModel.brandName}
                                        </Typography>
                                        <ProductDescription
                                            product={productContext}
                                            extendedStyles={styles.productDescription}
                                        />
                                        <Typography {...styles.productNumberText}>
                                            {product.autocompleteItemModel.erpNumber}
                                        </Typography>
                                    </GridItem>
                                    <GridItem {...styles.priceAndQuantityGridItem}>
                                        <GridContainer {...styles.priceAndQuantityContainer}>
                                            <GridItem {...styles.priceGridItem}>
                                                <OrderHistoryProductQuantityOrdered
                                                    extendedStyles={styles.quantityOrdered}
                                                    maxQtyOrdered={product.maxQtyOrdered}
                                                    disabled={product.autocompleteItemModel.rmaNeedsSn}
                                                />
                                                <TextField
                                                    {...styles.invoiceNumberTextField}
                                                    label={translate("Invoice Number")}
                                                    maxLength={20}
                                                    onChange={evt =>
                                                        changeRmaLineInvoiceNumber({
                                                            LineId: product.lineId,
                                                            InvoiceNumber: evt.target.value,
                                                        })
                                                    }
                                                />
                                                {product.autocompleteItemModel.rmaNeedsSn && (
                                                    <TextField
                                                        {...styles.serialNumberTextField}
                                                        label={translate("Serial Number")}
                                                        maxLength={20}
                                                        onChange={evt =>
                                                            changeRmaLineSerialNumber({
                                                                LineId: product.lineId,
                                                                SerialNumber: evt.target.value,
                                                            })
                                                        }
                                                    />
                                                )}
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...styles.returnReasonGridItem}>
                                <RadioGroup
                                    label={translate("Return Reason")}
                                    value={getCurrentLineReasonValue(product.lineId)}
                                    onChangeHandler={e => reasonsHandler(e.target.value, product.lineId)}
                                >
                                    {Object.entries(rmaReasons).map(([key, value]) => (
                                        <Radio key={key}>{value}</Radio>
                                    ))}
                                </RadioGroup>
                            </GridItem>
                            <GridItem {...styles.productActionsGridItem}>
                                <Clickable
                                    {...styles.removeProductClickable}
                                    onClick={() => removeProductHandler(product.lineId)}
                                    data-test-selector="orderHistory_RemoveProduct"
                                >
                                    <IconMemo {...styles.removeProductIcon} />
                                </Clickable>
                                <Button onClick={() => addAgainHandler(product)}>{translate("Add again")}</Button>
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                </CardContainer>
            </ProductContext.Provider>
        );
    });

    return (
        <>
            <StyledSection {...styles.wrapper}>
                <CardList>{productListDisplay}</CardList>
            </StyledSection>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.uploadFileGridItem}>
                    <RmaUploadFile />
                </GridItem>
                <GridItem {...styles.notesLine}>
                    <TextArea
                        label={translate("Return Notes")}
                        {...styles.editableNotesTextArea}
                        onChange={notesChangeHandler}
                        onBlur={notesBlurHandler}
                        value={rmaNotes}
                    />
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RmaOrderHistoryProductList),
    definition: {
        group: "RMA" as any,
        icon: "List",
    },
};

export default widgetModule;
